/**
 * Precast Panel Tool (BIM: Precast > Split > Precast Wall — direct place)
 *
 * Two-click placement of a single precast wall panel: first click sets the
 * start point (snapped on the ground plane), second sets the end. Height and
 * thickness come from the tool properties, joint gaps from the Configuration.
 * The tool stays active for a run of panels; Esc ends it. Complements the
 * Split tool, which derives panels from an existing monolithic wall.
 */
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import { PrecastWallElement } from '../../core/types';
import { getPrecastConfig } from '../../state/usePrecastConfigStore';
import i18n from '../../i18n';

export class PrecastPanelTool extends BaseTool {
    id = 'precast_panel';
    name = 'Precast Panel';
    cursor = 'crosshair';

    private start: THREE.Vector3 | null = null;
    private panelCounter = 0;

    private get heightM(): number {
        return getPrecastConfig().maxPanelHeightMm / 1000;
    }

    private get thicknessM(): number {
        // Wall thickness default mirrors a 200 mm precast sandwich panel.
        return 0.2;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        const snapped = this.snapToGrid(new THREE.Vector3(point.x, point.y, 0));

        if (!this.start) {
            this.start = snapped;
            return;
        }
        if (this.start.distanceTo(snapped) < 0.1) {
            this.notify(i18n.t('tools.notify.precastPanelEndPointTooClose'), 'warning');
            return;
        }
        this.placePanel(this.start, snapped);
        // Chain: the next panel starts where this one ended (BIM behavior).
        this.start = snapped;
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.start) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        this.updatePreview(this.snapToGrid(new THREE.Vector3(point.x, point.y, 0)));
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.start = null;
            this.context?.appStore.setToolPreview(null);
        }
        super.onKeyDown(event);
    }

    deactivate(): void {
        this.start = null;
        super.deactivate();
    }

    private placePanel(start: THREE.Vector3, end: THREE.Vector3): void {
        if (!this.context) return;
        const config = getPrecastConfig();
        const gap = Math.max(config.jointGapMm, 0) / 1000;
        this.panelCounter += 1;
        const mark = `WP-D${this.panelCounter}`;
        const baseZ = 0;
        const panel: PrecastWallElement = {
            id: uuidv4(),
            name: mark,
            category: 'Precast Walls',
            type: 'Precast Wall Panel',
            parameters: {
                'Mark': mark,
                'Panel Type': 'Precast Wall Panel',
                'Manufacturing Status': 'To Fabricate',
            },
            constraints: [],
            geometryData: {
                start: [start.x, start.y, baseZ],
                end: [end.x, end.y, baseZ],
                height: this.heightM,
                thickness: this.thicknessM,
                baseOffset: baseZ,
                jointWidth: gap,
                panelIndex: this.panelCounter,
                panelCount: 0, // free placement, not from a split set
                mark,
            },
        };
        this.context.bimStore.addElement(panel);
        this.notify(i18n.t('tools.notify.precastPanelPlaced', { mark: mark }), 'success');
    }

    private updatePreview(current?: THREE.Vector3): void {
        if (!this.context) return;
        if (!this.start) {
            this.context.appStore.setToolPreview(null);
            return;
        }
        const end = current ?? this.start;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.start,
            end,
            color: '#c9a227',
        });
    }
}
